/*
  Product page and quick add (spec chapters 14 and 16, update 2 chapter 6).
  <amor-product-form> runs the buying column in both places. The quick add
  drawer is filled from /products/<handle>?section_id=quick-add when a card's
  plus sends "quick-add:open". Storage goes only through window.DemoStore.
*/
(() => {
  // In milliseconds; the minified CSS on the store writes "400ms" as ".4s" (stage א12).
  const durationOf = (name, fallback) => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return fallback;
    return /ms$/i.test(raw) ? value : /s$/i.test(raw) ? value * 1000 : value;
  };

  /* -------------------------------------------------------------------------
     Cart count in the header, and the add-to-cart request.
  ------------------------------------------------------------------------- */
  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  // `properties`: line properties, e.g. a Gift Card's recipient (stage א6).
  const addToCart = async (variantId, handle, properties) => {
    const item = { id: variantId, quantity: 1 };
    if (properties) item.properties = properties;
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!response.ok) throw new Error('add failed');
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((res) => res.json());
    updateCartCount(cart.item_count);
    // cart.js opens the cart drawer with "goes well with" for this product.
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { variantId, handle, cart } }));
    return cart;
  };

  /* -------------------------------------------------------------------------
     Gallery: shows the selected color's images, the progress line and the
     "1/4" counter on the phone.
  ------------------------------------------------------------------------- */
  const galleryFor = (form) => form.closest('.product, .quick-add')?.querySelector('[data-gallery]');

  const visibleSlides = (gallery) => [...gallery.querySelectorAll('[data-gallery-slide]')].filter((slide) => !slide.hidden);

  const syncGalleryPosition = (gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = visibleSlides(gallery);
    if (!track || !slides.length) return;
    const index = Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / (track.clientWidth || 1))));
    const counter = gallery.querySelector('[data-gallery-counter]');
    const bar = gallery.querySelector('[data-gallery-bar]');
    if (counter) counter.textContent = `${index + 1}/${slides.length}`;
    if (bar) bar.style.width = `${((index + 1) / slides.length) * 100}%`;
    // One image in this color: nothing to count or swipe to (full pass, stage א7)
    gallery.classList.toggle('is-single', slides.length < 2);
  };

  const showColorInGallery = (gallery, color, hasColors) => {
    if (!gallery) return;
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const matching = slides.filter((slide) => slide.dataset.color === color);
    slides.forEach((slide) => {
      slide.hidden = hasColors && matching.length > 0 ? slide.dataset.color !== color : false;
    });
    const track = gallery.querySelector('[data-gallery-track]');
    if (track) track.scrollLeft = 0;
    syncGalleryPosition(gallery);
  };

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target.closest?.('[data-gallery-track]');
      if (track) syncGalleryPosition(track.closest('[data-gallery]'));
    },
    true
  );

  /* -------------------------------------------------------------------------
     Lightbox for the product page: the tapped image, large, with previous
     and next among the selected color's images.
  ------------------------------------------------------------------------- */
  const lightbox = {
    open(slide) {
      const dialog = document.querySelector('[data-lightbox]');
      if (!dialog) return;
      this.dialog = dialog;
      this.slides = visibleSlides(slide.closest('[data-gallery]'));
      this.index = this.slides.indexOf(slide);
      this.trigger = slide.querySelector('[data-gallery-zoom]');
      this.show();
      dialog.showModal();
      document.documentElement.classList.add('is-locked');
    },
    show() {
      const img = this.slides[this.index]?.querySelector('img');
      const target = this.dialog.querySelector('[data-lightbox-image]');
      if (!img || !target) return;
      target.src = img.currentSrc || img.src;
      target.srcset = img.srcset;
      target.sizes = '100vw';
    },
    step(direction) {
      if (!this.slides?.length) return;
      this.index = (this.index + direction + this.slides.length) % this.slides.length;
      this.show();
    },
    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      document.documentElement.classList.remove('is-locked');
      this.trigger?.focus({ preventScroll: true });
    },
  };

  document.addEventListener('click', (event) => {
    const zoom = event.target.closest('[data-gallery-zoom]');
    if (zoom && zoom.closest('.gallery--page')) {
      lightbox.open(zoom.closest('[data-gallery-slide]'));
      return;
    }
    if (event.target.closest('[data-lightbox-close]') || event.target.matches?.('[data-lightbox]')) lightbox.close();
    const step = event.target.closest('[data-lightbox-step]');
    if (step) lightbox.step(Number(step.dataset.lightboxStep));
  });
  document.addEventListener('keydown', (event) => {
    if (!lightbox.dialog?.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      lightbox.close();
    } else if (event.key === 'ArrowRight') lightbox.step(1);
    else if (event.key === 'ArrowLeft') lightbox.step(-1);
  });

  /* -------------------------------------------------------------------------
     <amor-product-form>: color, size, stock, add to cart, notify me.
  ------------------------------------------------------------------------- */
  class AmorProductForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.context = this.dataset.context;
      this.hasColors = this.data.colorIndex !== null;
      this.hasSizes = this.data.sizeIndex !== null;
      this.color = this.querySelector('[data-color-value][aria-pressed="true"]')?.dataset.colorValue
        || this.querySelector('[data-color-value]')?.dataset.colorValue
        || null;
      this.size = null;

      this.addEventListener('click', (event) => {
        const swatch = event.target.closest('[data-color-value]');
        if (swatch) this.selectColor(swatch.dataset.colorValue);
        const pill = event.target.closest('[data-size-value]');
        if (pill) this.selectSize(pill.dataset.sizeValue);
        if (event.target.closest('[data-add-button]')) this.add();
        if (event.target.closest('[data-notify-open]')) this.openNotify();
        const guide = event.target.closest('.product-info__guide');
        if (guide) this.highlightGuide(guide.dataset.drawerOpen);
      });
      this.querySelector('[data-notify-form]')?.addEventListener('submit', (event) => this.submitNotify(event));

      this.selectColor(this.color, true);
      if (this.context === 'page') {
        window.DemoStore?.addRecentlyViewed(this.data.handle);
        this.setupStickyBar();
      }
    }

    variantFor(color, size) {
      return this.data.variants.find((variant) => {
        if (this.hasColors && variant.options[this.data.colorIndex] !== color) return false;
        if (this.hasSizes && variant.options[this.data.sizeIndex] !== size) return false;
        return true;
      });
    }

    get variant() {
      if (this.hasSizes && !this.size) return null;
      return this.variantFor(this.color, this.size);
    }

    selectColor(color, initial) {
      this.color = color;
      this.querySelectorAll('[data-color-value]').forEach((swatch) => {
        const selected = swatch.dataset.colorValue === color;
        swatch.setAttribute('aria-pressed', String(selected));
        swatch.classList.toggle('is-selected', selected);
      });
      const name = this.querySelector('[data-color-name]');
      if (name && color) name.textContent = color;
      if (!initial || this.hasColors) showColorInGallery(galleryFor(this), color, this.hasColors);
      else if (galleryFor(this)) syncGalleryPosition(galleryFor(this));

      // Sizes that are sold out in this color: struck through, still selectable (they lead to "notify me").
      this.querySelectorAll('[data-size-value]').forEach((pill) => {
        const variant = this.variantFor(color, pill.dataset.sizeValue);
        pill.classList.toggle('is-unavailable', !variant || !variant.available);
      });
      if (this.size && !this.variantFor(color, this.size)) this.size = null;
      this.update();
    }

    selectSize(size) {
      this.size = size;
      this.querySelectorAll('[data-size-value]').forEach((pill) =>
        pill.setAttribute('aria-pressed', String(pill.dataset.sizeValue === size))
      );
      this.querySelector('[data-size-group]')?.classList.remove('is-invalid');
      const message = this.querySelector('[data-size-message]');
      if (message) message.hidden = true;
      this.update();
    }

    update() {
      const variant = this.variant;
      const input = this.querySelector('[data-variant-input]');
      if (input && variant) input.value = variant.id;

      // Low stock after the size is chosen.
      const low = this.querySelector('[data-low-stock]');
      if (low) {
        const qty = variant && variant.available ? variant.qty : 0;
        low.hidden = !(qty > 0 && qty <= 2);
        if (!low.hidden) low.textContent = qty === 1 ? low.dataset.textOne : low.dataset.textTwo;
      }

      // SKU of the selected variant in Details & Fabric.
      const sku = this.querySelector('[data-sku]');
      if (sku && variant) sku.textContent = variant.sku;

      // The add button turns into "notify me" for a sold-out size.
      const button = this.querySelector('[data-add-button]');
      if (button) {
        const soldOut = variant && !variant.available;
        button.querySelector('[data-add-label]').textContent = soldOut ? this.dataset.labelNotify : this.dataset.labelAdd;
        button.querySelector('[data-add-price]').hidden = Boolean(soldOut);
        button.dataset.mode = soldOut ? 'notify' : 'add';
        if (!soldOut) this.closeNotify();
      }

      // Boutique row: available for fitting, or can be ordered for a fitting.
      const row = this.querySelector('[data-boutique-row]');
      if (row) {
        const inBoutique = variant ? variant.inBoutique !== false : true;
        const status = row.querySelector('[data-boutique-status]');
        status.textContent = inBoutique ? row.dataset.textYes : row.dataset.textNo;
        status.classList.toggle('status--ok', inBoutique);
        status.classList.toggle('status--out', !inBoutique);
        row.querySelector('[data-boutique-link]').textContent = inBoutique ? row.dataset.linkYes : row.dataset.linkNo;
      }

      document.querySelectorAll('[data-sticky-size]').forEach((element) => {
        element.textContent = this.size ? `· ${this.size}` : '';
      });
    }

    askForSize() {
      const group = this.querySelector('[data-size-group]');
      group?.classList.add('is-invalid');
      const message = this.querySelector('[data-size-message]');
      if (message) {
        message.textContent = this.dataset.messageChooseSize;
        message.hidden = false;
      }
      group?.scrollIntoView({ block: 'center', behavior: window.AmorMotion?.scroll() || 'smooth' });
      this.querySelector('[data-size-value]')?.focus({ preventScroll: true });
    }

    async add() {
      if (this.hasSizes && !this.size) {
        this.askForSize();
        return;
      }
      const button = this.querySelector('[data-add-button]');
      if (button?.dataset.mode === 'notify') {
        this.openNotify();
        return;
      }
      const variant = this.variant;
      if (!variant || this.busy) return;
      this.busy = true;
      const message = this.querySelector('[data-add-message]');
      if (message) message.hidden = true;
      button.classList.add('is-loading');
      try {
        await addToCart(variant.id, this.data.handle);
        const label = button.querySelector('[data-add-label]');
        label.textContent = this.dataset.labelAdded;
        window.setTimeout(() => {
          label.textContent = this.dataset.labelAdd;
        }, 1600);
        if (this.context === 'quick') this.closest('amor-drawer')?.close();
      } catch {
        if (message) {
          message.textContent = this.dataset.messageFailed;
          message.hidden = false;
        }
      } finally {
        this.busy = false;
        button.classList.remove('is-loading');
      }
    }

    openNotify() {
      const form = this.querySelector('[data-notify-form]');
      if (!form) return;
      const done = this.querySelector('[data-notify-done]');
      const variant = this.variant;
      if (window.DemoStore?.hasNotifyRequest(this.data.handle, variant?.id || null)) {
        this.showNotifyDone();
        return;
      }
      if (done) done.hidden = true;
      form.hidden = false;
      const input = form.querySelector('[data-notify-input]');
      // Signed in, the form comes with her phone (spec chapter 16).
      if (input && !input.value && window.DemoStore?.signedIn()) input.value = window.DemoStore.details().phone || '';
      input?.focus();
    }

    closeNotify() {
      const form = this.querySelector('[data-notify-form]');
      if (form) form.hidden = true;
      const done = this.querySelector('[data-notify-done]');
      if (done) done.hidden = true;
    }

    showNotifyDone() {
      const done = this.querySelector('[data-notify-done]');
      const form = this.querySelector('[data-notify-form]');
      if (form) form.hidden = true;
      if (!done) return;
      done.textContent = this.hasSizes && this.size ? done.dataset.messageSize : done.dataset.messageProduct;
      done.hidden = false;
    }

    submitNotify(event) {
      event.preventDefault();
      const input = this.querySelector('[data-notify-input]');
      const error = this.querySelector('[data-notify-error]');
      const value = input.value.trim();
      const email = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
      const phone = /^0\d{8,9}$/.test(value.replace(/[\s-]/g, ''));
      if (!email && !phone) {
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      window.DemoStore?.addNotifyRequest({ handle: this.data.handle, variantId: this.variant?.id || null, contact: value });
      this.showNotifyDone();
    }

    highlightGuide(drawerId) {
      const drawer = document.getElementById(drawerId);
      if (!drawer) return;
      drawer.querySelectorAll('[data-sizes]').forEach((row) => {
        row.classList.toggle('is-selected', Boolean(this.size) && row.dataset.sizes.split('|').includes(this.size));
      });
    }

    // Phone only: when the add button leaves the screen, a bar with the name,
    // price, chosen size and an add button appears at the bottom.
    setupStickyBar() {
      const bar = document.querySelector('[data-sticky-bar]');
      const button = this.querySelector('[data-add-button]');
      if (!bar || !button || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(button);
      bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.add());
    }
  }
  customElements.define('amor-product-form', AmorProductForm);

  /* -------------------------------------------------------------------------
     Quick add: the card's plus opens the shared drawer with the product.
  ------------------------------------------------------------------------- */
  document.addEventListener('quick-add:open', async (event) => {
    const drawer = document.getElementById('QuickAddDrawer');
    if (!drawer) return;
    event.preventDefault();
    const { handle, variant, trigger } = event.detail;
    const body = drawer.querySelector('[data-quick-add-body]');
    body.innerHTML = drawer.querySelector('[data-quick-add-skeleton]').innerHTML;
    drawer.open(trigger);
    try {
      const root = window.Shopify?.routes?.root || '/';
      // A card shown in one color opens in that color.
      const chosen = variant ? `&variant=${encodeURIComponent(variant)}` : '';
      const html = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=quick-add${chosen}`).then((res) => {
        if (!res.ok) throw new Error('quick add failed');
        return res.text();
      });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-quick-add-content]');
      if (!content) throw new Error('quick add failed');
      body.replaceChildren(content);
      document.dispatchEvent(new CustomEvent('demo-store:change', { detail: { key: 'wishlist' } }));
      const gallery = content.querySelector('[data-gallery]');
      if (gallery) syncGalleryPosition(gallery);
      // A sold-out item's plus on the Wishlist page leads to "notify me" (spec chapter 20).
      if (trigger?.hasAttribute?.('data-quick-add-notify')) content.querySelector('[data-notify-open]')?.click();
    } catch {
      body.innerHTML = `<p class="field-message field-message--error">${drawer.dataset.messageFailed}</p>`;
    }
  });

  // Pull the quick-add sheet down to close it (phone).
  document.addEventListener(
    'touchstart',
    (event) => {
      const panel = event.target.closest('#QuickAddDrawer .drawer__panel');
      if (!panel || panel.querySelector('.drawer__body').scrollTop > 0) return;
      const startY = event.touches[0].clientY;
      const onEnd = (end) => {
        const delta = end.changedTouches[0].clientY - startY;
        if (delta > 90) document.getElementById('QuickAddDrawer').close();
        panel.removeEventListener('touchend', onEnd);
      };
      panel.addEventListener('touchend', onEnd);
    },
    { passive: true }
  );

  /* -------------------------------------------------------------------------
     Recently Viewed: shown from the second product viewed, without the
     current one. Cards come from ?section_id=product-card-fragment.
  ------------------------------------------------------------------------- */
  const loadRecent = async () => {
    const section = document.querySelector('[data-recent]');
    if (!section || !window.DemoStore) return;
    const handles = window.DemoStore.recentlyViewed().filter((handle) => handle !== section.dataset.current).slice(0, 4);
    if (!handles.length) return;
    const root = window.Shopify?.routes?.root || '/';
    const cards = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}?section_id=product-card-fragment`)
          .then((res) => (res.ok ? res.text() : ''))
          .catch(() => '')
      )
    );
    const list = section.querySelector('[data-recent-list]');
    cards.forEach((html) => {
      const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.card');
      if (!card) return;
      const item = document.createElement('li');
      item.append(card);
      list.append(item);
    });
    if (list.children.length) {
      section.hidden = false;
      document.dispatchEvent(new CustomEvent('demo-store:change', { detail: { key: 'wishlist' } }));
    }
  };

  const init = () => {
    loadRecent();
    document.querySelectorAll('[data-gallery]').forEach(syncGalleryPosition);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.AmorCart = { updateCartCount, addToCart, durationOf };
})();
